<template>
  <div class="data-table-controller">
    <div class="data-table-controller__table-controllers table-controllers">
      <div class="data-table-controller__table-controllers--buttons">
        <button
          class="data-table-controller__table-controllers--button"
          @click="exportToExcel"
        >
          Export
        </button>

        <file-uploader
          :validExtensions="['.csv', '.xlsx', '.xls']"
          uploader-key="IMPORT"
          @upload="handleFileUpload"
        >
          <span class="data-table-controller__table-controllers--button">
            Import
          </span>
        </file-uploader>
      </div>
      
      <div class="data-table-controller__table-controllers--buttons">
        <button
          class="data-table-controller__table-controllers--button"
          @click="addRow"
        >
          Add row
        </button>

        <button
          class="data-table-controller__table-controllers--button"
          @click="deleteCurrentRow"
          :disabled="String(selectedRowKey) === 'undefined' || String(selectedRowKey) === 'null'"
        >
          Delete row
        </button>
      </div>
    </div>

    <sheet-pagination
      :current-surface="currentSurface"
      :surface-count="surfaceCount"
      @prev-sheet="prevSheet"
      @next-sheet="nextSheet"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watchEffect } from 'vue';

import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

import TableRowModel from '../../models/table-row.model';

import FileUploader from '../ui/FileUploader.vue';
import SheetPagination from '../ui/SheetPagination.vue';

import { E_DATA_SHEET_CONTROLLER_EVENT } from '../../types/components-events.enums';
import type { IDataSet } from '../../types/table.types';
import uuid4 from '../../utils/uuid4';
import type { TDataSheetControllerEventResponse } from '../../types/event-response.types';

interface IProps {
  selectedRowKey: string | null;
  currentSurface: number;
  tableData: IDataSet[];
};
const props = defineProps<IProps>();
const emit = defineEmits<{
  (event: E_DATA_SHEET_CONTROLLER_EVENT.UPDATE_SELECTED_ROW_KEY, payload: TDataSheetControllerEventResponse[E_DATA_SHEET_CONTROLLER_EVENT.UPDATE_SELECTED_ROW_KEY]): void;
  (event: E_DATA_SHEET_CONTROLLER_EVENT.UPDATE_ROWS, payload: TDataSheetControllerEventResponse[E_DATA_SHEET_CONTROLLER_EVENT.UPDATE_ROWS]): void;
  (event: E_DATA_SHEET_CONTROLLER_EVENT.UPDATE_CURRENT_SURFACE, payload: TDataSheetControllerEventResponse[E_DATA_SHEET_CONTROLLER_EVENT.UPDATE_CURRENT_SURFACE]): void;
  (event: E_DATA_SHEET_CONTROLLER_EVENT.CLEAR_SHEETS, payload: TDataSheetControllerEventResponse[E_DATA_SHEET_CONTROLLER_EVENT.CLEAR_SHEETS]): void;
}>();

const _tableData = ref(props.tableData);
const _currentSurface = ref(props.currentSurface);
const _selectedRowKey = ref(props.selectedRowKey);

const surfaceCount = computed(() => _tableData.value.length);

watchEffect(() => {
  _currentSurface.value = props.currentSurface;
});
watchEffect(() => {
  _tableData.value = props.tableData;
});
watchEffect(() => {
  _selectedRowKey.value = props.selectedRowKey;
});

function exportToExcel() {
  const exportData = _tableData.value.map((table) => table.rows.map(row => row.row));
  const workbook = XLSX.utils.book_new();
  exportData.forEach((table, tableIndex) => {
    const worksheet = XLSX.utils.json_to_sheet(table);
    XLSX.utils.book_append_sheet(workbook, worksheet, `Sheet-${tableIndex}`);
  });
  const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
  const blob = new Blob([excelBuffer], { type: 'application/octet-stream' });
  saveAs(blob, 'data-export.xlsx');
}

function handleFileUpload(handleOptions: { tables: Record<string, any>[][], surfacesColumns: string[][] }) {
  clearSheets();
  handleOptions.tables.forEach((_, tableIndex) => {
    const newRows = handleOptions.tables[tableIndex].map((row) => ({
      key: uuid4(),
      row: row,
    }));
    _tableData.value[tableIndex] ??= {
      columns: handleOptions.surfacesColumns[tableIndex],
      rows: [],
    };
    if (_tableData.value[tableIndex].columns.length === 0) {
      _tableData.value[tableIndex].columns = handleOptions.surfacesColumns[tableIndex];
    }
    _tableData.value[tableIndex].rows = newRows;
    emit(E_DATA_SHEET_CONTROLLER_EVENT.UPDATE_ROWS, {
      rows: _tableData.value[tableIndex].rows,
      columns: _tableData.value[tableIndex].columns,
      surfaceIndex: tableIndex,
    });
  });
}

function clearSheets() {
  _tableData.value = [{
    columns: [],
    rows: [],
  }];
  _currentSurface.value = 0;
  emit(E_DATA_SHEET_CONTROLLER_EVENT.UPDATE_CURRENT_SURFACE, {
    surfaceIndex: _currentSurface.value,
  });
  unsetSelectedRow();
  emit(E_DATA_SHEET_CONTROLLER_EVENT.CLEAR_SHEETS);
}

function addRow() {
  const newRow = new TableRowModel(_tableData.value[_currentSurface.value].columns).value;
  _tableData.value[_currentSurface.value].rows.push({
    key: uuid4(),
    row: newRow,
  });
  emit(E_DATA_SHEET_CONTROLLER_EVENT.UPDATE_ROWS, {
    rows: _tableData.value[_currentSurface.value].rows,
    columns: _tableData.value[_currentSurface.value].columns,
    surfaceIndex: _currentSurface.value,
  });
}

function deleteCurrentRow() {
  if (_selectedRowKey.value !== null) {
    deleteRow(_selectedRowKey.value, _currentSurface.value);
    unsetSelectedRow();
  }
}

function deleteRow(key: string, surfaceIndex: number) {
  _tableData.value[surfaceIndex].rows = _tableData.value[surfaceIndex].rows.filter(item => item.key !== key);
  emit(E_DATA_SHEET_CONTROLLER_EVENT.UPDATE_ROWS, {
    rows: _tableData.value[surfaceIndex].rows,
    columns: _tableData.value[surfaceIndex].columns,
    surfaceIndex: surfaceIndex,
  });
}

function prevSheet() {
  _currentSurface.value--;
  emit(E_DATA_SHEET_CONTROLLER_EVENT.UPDATE_CURRENT_SURFACE, {
    surfaceIndex: _currentSurface.value,
  });
  unsetSelectedRow();
}

function nextSheet() {
  _currentSurface.value++;
  emit(E_DATA_SHEET_CONTROLLER_EVENT.UPDATE_CURRENT_SURFACE, {
    surfaceIndex: _currentSurface.value,
  });
  unsetSelectedRow();
}

function unsetSelectedRow() {
  _selectedRowKey.value = null;
  emit(E_DATA_SHEET_CONTROLLER_EVENT.UPDATE_SELECTED_ROW_KEY, {
    rowKey: _selectedRowKey.value,
    surfaceIndex: _currentSurface.value,
  });
}
</script>

<style scoped lang="scss">
.data-table-controller {
  display: flex;
  justify-content: space-between;
  padding: 16px 0;

  &__table-controllers {
    display: flex;
    gap: 16px;

    &--buttons {
      display: flex;
      gap: 0;
    }

    &--button {
      font-family: 'Inter', sans-serif;
      font-weight: 600;
      font-size: 14px;
      line-height: 20px;
      letter-spacing: 0%;
      text-align: center;
      color: $brand-color;
      border: none;
      background: none;
      padding: 8px;
      cursor: pointer;

      &:disabled {
        color: #b2b2b2a3;
        cursor: default;
      }
    }
  }
}

@media screen and (max-width: 768px) {
  .data-table-controller {
    flex-wrap: wrap-reverse;
    align-items: center;
    justify-content: center;
    padding: 8px 0;
  }
}
</style>